import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { authService } from "../../services/auth.service";
import BloodGroupPicker from "../../components/userProfile/BloodGroupPicker";
import PincodeAutoFill from "../../components/common/PincodeAutoFill";
import ManualDobFields from "../../components/common/ManualDobFields";
import {
  partsToDobIso,
  validateDobParts,
} from "../../utils/dateOfBirthUtils";
import "../../styles/users.css";
import "../../styles/profile.css";
import "../../styles/agentRegister.css";

const IDENTITY_MAX_BYTES = 5 * 1024 * 1024;
const IDENTITY_TYPES = new Set(["image/jpeg", "image/png", "application/pdf"]);
const PAN_REGEX = /^[A-Z]{5}[0-9]{4}[A-Z]$/i;

function isIdentityDocumentFile(file) {
  if (!file) return false;
  const mime = String(file.type || "").toLowerCase();
  const name = String(file.name || "").toLowerCase();
  const extOk = /\.(jpe?g|png|pdf)$/.test(name);
  if (extOk && (!mime || IDENTITY_TYPES.has(mime))) return true;
  return IDENTITY_TYPES.has(mime);
}

function isIdentityPdf(file) {
  if (!file) return false;
  const mime = String(file.type || "").toLowerCase();
  const name = String(file.name || "").toLowerCase();
  return mime === "application/pdf" || name.endsWith(".pdf");
}

export default function AgentRegister() {
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({
    companyCode: searchParams.get("code") || "",
    name: "",
    fatherName: "",
    email: "",
    phone: "",
    password: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    panNumber: "",
    aadhaarNumber: "",
    bloodGroup: "",
    dateOfBirth: "",
    role: "REPO_STAFF",
  });
  const [dobParts, setDobParts] = useState({
    day: "",
    month: "",
    year: "",
  });
  const [panDocumentFile, setPanDocumentFile] = useState(null);
  const [aadhaarDocumentFile, setAadhaarDocumentFile] = useState(null);
  const [panDocumentPreview, setPanDocumentPreview] = useState("");
  const [aadhaarDocumentPreview, setAadhaarDocumentPreview] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [registrationId, setRegistrationId] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) return undefined;
    const timer = window.setInterval(() => {
      setResendCooldown((value) => Math.max(0, value - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [resendCooldown]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let next = value;
    if (name === "pincode") {
      next = value.replace(/\D/g, "").slice(0, 6);
    } else if (name === "aadhaarNumber") {
      next = value.replace(/\D/g, "").slice(0, 12);
    } else if (name === "panNumber") {
      next = value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase().slice(0, 10);
    }
    setForm((prev) => ({ ...prev, [name]: next }));
  };

  const handleDobChange = (parts) => {
    setDobParts(parts);
    setForm((prev) => ({
      ...prev,
      dateOfBirth: partsToDobIso(parts),
    }));
  };

  const handleIdentityDocumentPick = (kind, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!isIdentityDocumentFile(file)) {
      setError("Upload a JPG, JPEG, PNG, or PDF file for PAN and Aadhaar.");
      return;
    }
    if (file.size > IDENTITY_MAX_BYTES) {
      setError("Each document must be 5 MB or smaller.");
      return;
    }
    const preview = isIdentityPdf(file) ? "" : URL.createObjectURL(file);
    if (kind === "pan") {
      setPanDocumentFile(file);
      setPanDocumentPreview(preview);
    } else {
      setAadhaarDocumentFile(file);
      setAadhaarDocumentPreview(preview);
    }
    setError("");
  };

  const resetOtpFlow = () => {
    setOtpSent(false);
    setRegistrationId("");
    setOtp("");
    setResendCooldown(0);
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!form.companyCode.trim() || !form.name.trim() || !form.email.trim()) {
      setError("Company code, name, and email are required.");
      return;
    }
    if (!form.fatherName.trim()) {
      setError("Father's name is required.");
      return;
    }
    if (!PAN_REGEX.test(form.panNumber.trim())) {
      setError("PAN number must be 10 characters (AAAAA9999A).");
      return;
    }
    if (form.aadhaarNumber.replace(/\D/g, "").length !== 12) {
      setError("Aadhaar number must be 12 digits.");
      return;
    }
    if (!panDocumentFile) {
      setError("PAN card image or PDF is required.");
      return;
    }
    if (!aadhaarDocumentFile) {
      setError("Aadhaar card image or PDF is required.");
      return;
    }
    if (!form.bloodGroup) {
      setError("Please select a blood group.");
      return;
    }
    const dobError = validateDobParts(dobParts);
    if (dobError) {
      setError(dobError);
      return;
    }
    if (form.password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (!form.address.trim() || !form.city.trim() || !form.state.trim()) {
      setError("Address, city, and state are required.");
      return;
    }
    if (!/^\d{6}$/.test(form.pincode)) {
      setError("Valid 6-digit pincode is required.");
      return;
    }

    setLoading(true);
    try {
      const payload = new FormData();
      payload.append("companyCode", form.companyCode.trim().toUpperCase());
      payload.append("name", form.name.trim());
      payload.append("fatherName", form.fatherName.trim());
      payload.append("email", form.email.trim());
      payload.append("phone", form.phone);
      payload.append("password", form.password);
      payload.append("address", form.address.trim());
      payload.append("city", form.city.trim());
      payload.append("state", form.state.trim());
      payload.append("pincode", form.pincode);
      payload.append("panNumber", form.panNumber.trim().toUpperCase());
      payload.append("aadhaarNumber", form.aadhaarNumber.replace(/\D/g, ""));
      payload.append("bloodGroup", form.bloodGroup);
      payload.append("dateOfBirth", partsToDobIso(dobParts));
      payload.append("role", form.role);
      payload.append("panDocument", panDocumentFile);
      payload.append("aadhaarDocument", aadhaarDocumentFile);

      const res = await authService.agentSelfRegister(payload);
      const data = res?.data || {};
      setRegistrationId(data.registrationId || "");
      setOtpSent(Boolean(res?.requiresOtp));
      setResendCooldown(Number(data.resendAvailableInSeconds || 60));
      if (!res?.requiresOtp) {
        setSuccess(res?.message || "Registration submitted.");
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Registration failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(otp)) {
      setError("Enter the 6-digit OTP received by SMS.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const res = await authService.verifyAgentRegistration(registrationId, otp);
      setOtpSent(false);
      setSuccess(res?.message || "Registration successful. Await admin activation.");
      setOtp("");
    } catch (err) {
      setError(err?.response?.data?.message || "OTP verification failed.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0 || loading) return;
    setLoading(true);
    setError("");
    try {
      const res = await authService.resendAgentRegistrationOtp(registrationId);
      setResendCooldown(Number(res?.data?.resendAvailableInSeconds || 60));
    } catch (err) {
      setError(err?.response?.data?.message || "Unable to resend OTP.");
      const match = String(err?.response?.data?.message || "").match(/(\d+)\s*seconds/);
      if (match) setResendCooldown(Number(match[1]));
    } finally {
      setLoading(false);
    }
  };

  const pageHeader = (
    <div className="create-company-header">
      <div>
        <h2>Agent self-registration</h2>
        <p className="muted">
          Use the company code from your repo admin. Your account stays
          inactive until the admin activates it.
        </p>
      </div>
      <Link to="/repo-agent/login" className="secondary-page-btn">
        Back to login
      </Link>
    </div>
  );

  return (
    <div className="page create-company-page repo-form-page agent-register-page">
      {!otpSent ? (
        <form className="create-company-form" onSubmit={handleSubmit}>
          <div className="create-company-card">
            {pageHeader}
            <section className="create-company-section" aria-labelledby="agent-agency">
              <header className="create-company-section__head" id="agent-agency">
                <h3>Agency</h3>
              </header>
              <div className="form-grid two-column">
                <div className="form-group">
                  <label htmlFor="companyCode">Company code *</label>
                  <input
                    id="companyCode"
                    name="companyCode"
                    value={form.companyCode}
                    onChange={handleChange}
                    placeholder="e.g. HIM1114"
                    required
                  />
                </div>
              </div>
            </section>

            <section className="create-company-section" aria-labelledby="agent-personal">
              <header className="create-company-section__head" id="agent-personal">
                <h3>Personal details</h3>
              </header>
              <div className="form-grid two-column">
                <div className="form-group">
                  <label htmlFor="name">Full name *</label>
                  <input
                    id="name"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Enter full name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="fatherName">Father&apos;s name *</label>
                  <input
                    id="fatherName"
                    name="fatherName"
                    value={form.fatherName}
                    onChange={handleChange}
                    placeholder="Father's name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">Email *</label>
                  <input
                    id="email"
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="you@example.com"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="phone">Mobile *</label>
                  <input
                    id="phone"
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="10-digit mobile number"
                    required
                  />
                </div>
                <div className="form-group form-group--dob">
                  <label>Date of birth *</label>
                  <ManualDobFields
                    value={form.dateOfBirth}
                    onChange={handleDobChange}
                    idPrefix="agent-register-dob"
                  />
                  <p className="field-hint muted">Format: DD / MM / YYYY</p>
                </div>
                <div className="form-group">
                  <label htmlFor="password">Password *</label>
                  <input
                    id="password"
                    type="password"
                    name="password"
                    value={form.password}
                    onChange={handleChange}
                    placeholder="Minimum 6 characters"
                    minLength={6}
                    required
                  />
                </div>
                <div className="form-group full-width">
                  <label>Blood group *</label>
                  <BloodGroupPicker
                    value={form.bloodGroup}
                    onChange={(bloodGroup) =>
                      setForm((prev) => ({ ...prev, bloodGroup }))
                    }
                    required
                  />
                </div>
              </div>
            </section>

            <section className="create-company-section" aria-labelledby="agent-identity">
              <header className="create-company-section__head" id="agent-identity">
                <h3>PAN &amp; Aadhaar</h3>
              </header>
              <div className="form-grid two-column">
                <div className="form-group">
                  <label htmlFor="panNumber">PAN Number *</label>
                  <input
                    id="panNumber"
                    name="panNumber"
                    value={form.panNumber}
                    onChange={handleChange}
                    placeholder="AAAAA9999A"
                    maxLength={10}
                    required
                    style={{ textTransform: "uppercase" }}
                  />
                  <div className="identity-upload">
                    <label htmlFor="agent-pan-document">Upload PAN Card *</label>
                    <input
                      id="agent-pan-document"
                      type="file"
                      accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
                      onChange={(e) => handleIdentityDocumentPick("pan", e)}
                      required={!panDocumentFile}
                    />
                    {panDocumentFile ? (
                      <div className="tax-doc-upload">
                        {panDocumentPreview ? (
                          <img
                            className="tax-doc-upload__preview"
                            src={panDocumentPreview}
                            alt=""
                          />
                        ) : null}
                        <span className="tax-doc-upload__name">
                          {panDocumentFile.name}
                        </span>
                      </div>
                    ) : (
                      <p className="field-hint muted">JPG, JPEG, PNG, or PDF</p>
                    )}
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="aadhaarNumber">Aadhaar Number *</label>
                  <input
                    id="aadhaarNumber"
                    name="aadhaarNumber"
                    value={form.aadhaarNumber}
                    onChange={handleChange}
                    placeholder="12-digit Aadhaar number"
                    inputMode="numeric"
                    maxLength={12}
                    required
                  />
                  <div className="identity-upload">
                    <label htmlFor="agent-aadhaar-document">
                      Upload Aadhaar Card *
                    </label>
                    <input
                      id="agent-aadhaar-document"
                      type="file"
                      accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
                      onChange={(e) => handleIdentityDocumentPick("aadhaar", e)}
                      required={!aadhaarDocumentFile}
                    />
                    {aadhaarDocumentFile ? (
                      <div className="tax-doc-upload">
                        {aadhaarDocumentPreview ? (
                          <img
                            className="tax-doc-upload__preview"
                            src={aadhaarDocumentPreview}
                            alt=""
                          />
                        ) : null}
                        <span className="tax-doc-upload__name">
                          {aadhaarDocumentFile.name}
                        </span>
                      </div>
                    ) : (
                      <p className="field-hint muted">JPG, JPEG, PNG, or PDF</p>
                    )}
                  </div>
                </div>
              </div>
            </section>

            <section className="create-company-section" aria-labelledby="agent-address">
              <header className="create-company-section__head" id="agent-address">
                <h3>Address</h3>
              </header>
              <div className="form-grid two-column">
                <div className="form-group full-width">
                  <label htmlFor="address">Address *</label>
                  <input
                    id="address"
                    name="address"
                    value={form.address}
                    onChange={handleChange}
                    placeholder="House no., street, area"
                    required
                  />
                </div>
                <PincodeAutoFill
                  pincode={form.pincode}
                  onPincodeChange={(value) =>
                    setForm((prev) => ({ ...prev, pincode: value }))
                  }
                  city={form.city}
                  onCityChange={(value) =>
                    setForm((prev) => ({ ...prev, city: value }))
                  }
                  state={form.state}
                  onStateChange={(value) =>
                    setForm((prev) => ({ ...prev, state: value }))
                  }
                  cityLabel="City"
                  pincodeId="agent-pincode"
                  cityId="agent-city"
                  stateId="agent-state"
                />
              </div>
            </section>

            {error && <p className="error-text">{error}</p>}
            {success && <p className="cfm-status">{success}</p>}

            <div className="create-company-actions">
              <Link to="/repo-agent/login" className="secondary-page-btn">
                Back to login
              </Link>
              <button type="submit" className="primary-page-btn" disabled={loading}>
                {loading ? "Submitting…" : "Register"}
              </button>
            </div>
          </div>
        </form>
      ) : (
        <form
          className="create-company-form agent-register-otp"
          onSubmit={handleVerifyOtp}
        >
          <div className="create-company-card">
            {pageHeader}
            <section className="create-company-section">
              <header className="create-company-section__head">
                <h3>Verify mobile number</h3>
              </header>
              <p className="muted">
                We sent a 6-digit OTP to {form.phone.replace(/\d(?=\d{4})/g, "*")}.
              </p>
              <div className="form-group">
                <label htmlFor="agent-register-otp">Enter OTP *</label>
                <input
                  id="agent-register-otp"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  autoFocus
                  value={otp}
                  onChange={(e) =>
                    setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))
                  }
                  placeholder="6-digit OTP"
                  required
                />
              </div>
              {resendCooldown > 0 ? (
                <p className="muted">Resend available in {resendCooldown}s</p>
              ) : null}
              {error && <p className="error-text">{error}</p>}
            </section>
            <div className="create-company-actions agent-register-otp-actions">
              <button
                type="button"
                className="secondary-page-btn"
                onClick={resetOtpFlow}
                disabled={loading}
              >
                Back / Edit
              </button>
              <button
                type="button"
                className="secondary-page-btn"
                onClick={handleResendOtp}
                disabled={loading || resendCooldown > 0}
              >
                Resend OTP
              </button>
              <button
                type="submit"
                className="primary-page-btn"
                disabled={loading || otp.length !== 6}
              >
                {loading ? "Verifying…" : "Verify OTP"}
              </button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
}
